import { Transition } from "@headlessui/react";
import { cx } from "@jugl-web/utils";
import { FC, ReactNode } from "react";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { PlainButton } from "../../primitives/PlainButton";
import { Button, ButtonProps } from "../../primitives/Button";

type DialogButtons = Array<
  Omit<ButtonProps, "ref"> & {
    text: string;
  }
>;

export interface InfoAlertDialogProps {
  isOpen: boolean;
  img: string | ReactNode;
  title: string;
  content: ReactNode;
  buttons: DialogButtons;
  position?: "bottom-right";
  onClose: () => void;
  onAfterClose?: () => void;
}

const classesByPosition = {
  "bottom-right": "right-6 bottom-6 origin-bottom-right",
};

/**
 * This component is primarily used to:
 * - Inform users about changes or updates in the application
 * - Introduce users to new modules or views
 * - Display important notifications that require user acknowledgment
 *
 * The dialog features a smooth transition animation, customizable content, and flexible button actions.
 */
export const InfoAlertDialog: FC<InfoAlertDialogProps> = ({
  isOpen,
  img,
  title,
  content,
  buttons,
  position = "bottom-right",
  onClose,
  onAfterClose,
}) => {
  return (
    <Transition
      as="div"
      show={isOpen}
      enter="transition"
      enterFrom="opacity-0 -translate-x-4 scale-75"
      enterTo="opacity-100 translate-x-0 scale-100"
      leave="transition"
      leaveFrom="opacity-100 translate-x-0 scale-100"
      leaveTo="opacity-0 -translate-x-4 scale-75"
      afterLeave={onAfterClose}
      className={cx(
        "fixed z-[200] flex max-h-[calc(100vh-48px)] w-[420px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_56px_rgba(0,0,0,0.24)] duration-300",
        classesByPosition[position]
      )}
    >
      <PlainButton
        className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-lg bg-[#6B738A]/20 transition-colors hover:bg-[#6B738A]/40"
        onClick={onClose}
      >
        <CloseIcon />
      </PlainButton>
      <div className="pointer-events-none h-[280px] w-full shrink-0 overflow-hidden">
        {typeof img === "string" ? (
          <img
            src={img}
            alt={title}
            className="h-full w-full object-cover object-center"
          />
        ) : (
          img
        )}
      </div>
      <div className="jugl__custom-scrollbar flex flex-col overflow-y-auto px-6 pt-7">
        <span className="shrink-0 truncate text-center font-secondary text-[20px] leading-[30px] font-semibold text-dark">
          {title}
        </span>
        <div className="mt-6 font-secondary text-sm leading-[150%] text-dark">
          {content}
        </div>
      </div>
      <div className="my-10 flex shrink-0 items-center justify-center gap-6 px-8">
        {buttons.map(({ text, role, className, ...buttonProps }) => (
          <Button
            key={text}
            onClick={(e) => {
              if (buttonProps?.onClick) {
                buttonProps.onClick(e);
                return;
              }
              if (role === "close") {
                onClose();
              }
            }}
            className={cx("h-10 w-full max-w-[200px]", className)}
            {...buttonProps}
          >
            {text}
          </Button>
        ))}
      </div>
    </Transition>
  );
};
